import { A, pipe } from "@mobily/ts-belt"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { extractRangeIncludingBlanks } from "cc-frontend/lib/transferrable-date-utils"
import {
  consolidateCourseDatesOff,
  constructUnitsAndDates,
  insertRange,
} from "cc-frontend/lib/unit-utils"
import { cloneDeep, forEach, maxBy } from "lodash-es"
import { filterCourseDateDefault, isCourseDateCustom } from "../filter-dates/course"
import { pluckNewRotationIds } from "../filter-dates/rotation-calendar"

export function updateCourseToRotationCalendar(
  sourceRotationCalendar: RotationCalendar,
  destinationRotationCalendar: RotationCalendar,
  sourcePlanbook: Planbook,
  destinationPlanbook: Planbook,
  course: Course
) {
  let effectiveDate = sourceRotationCalendar.attributes.datesLastChangedOn
  if (effectiveDate === null || effectiveDate === undefined || typeof effectiveDate !== "string") {
    // TODO This is a truly terribly named variable that does something very different than what it sounds
    // Originally, it was supposed to see if something was updated to the most recent version of a rotationcalendar
    // At least, that's my memory and how it sounds. In practice, it sounds like a variable we pass around
    // to say from which date to change dates. This NEEDS to be resolved.
    throw new Error("Old Rotation Calendar needs a `datesLastChangedOn` Property")
  }

  // Update the course except for the course-date-customs after effectiveDate.
  const updatedDefaultDates = filterCourseDateDefault(course)
  const newRotationIds = pluckNewRotationIds(sourceRotationCalendar, updatedDefaultDates)
  if (newRotationIds.length > 0) {
    const semesterId = sourceRotationCalendar.attributes.semesters[0].id
    forEach(newRotationIds, (rotationId) => {
      updatedDefaultDates.push({
        id: `${semesterId}:${rotationId}`,
        type: "course-date-default",
        attributes: {
          cardStackId: null,
          rotationId: rotationId,
          semesterId: semesterId,
          time: null,
        },
      })
    })
  }
  const updatedCoursePart1 = cloneDeep(course)
  updatedCoursePart1.attributes.calendar.dates = [
    ...updatedDefaultDates,
    ...course.attributes.calendar.dates.filter(
      (courseDate) =>
        isCourseDateCustom(courseDate) && courseDate.attributes.dateString < effectiveDate
    ),
  ]
  // Clear the units for now. They'll be reconstructed later and the old ones confuse consolidateCourseDatesOff.
  updatedCoursePart1.attributes.calendar.units = []

  // Extract all course-date-customs from the effectiveDate onwards (including blanks)
  // and convert them to a transferrable array.
  const courseEndDate = pipe(
    course.attributes.calendar.dates,
    A.filter(isCourseDateCustom),
    A.filter(
      (courseDate) =>
        courseDate.attributes.cardStackId !== null || courseDate.attributes.unitIds.length > 0
    ),
    (courseDates) => maxBy(courseDates, (courseDate) => courseDate.attributes.dateString)
  )
  const courseEndDateString =
    courseEndDate && courseEndDate.attributes.dateString > effectiveDate
      ? courseEndDate.attributes.dateString
      : effectiveDate
  const sourceDatesOff = consolidateCourseDatesOff(course, sourcePlanbook, sourceRotationCalendar)
  const transferrableArray = extractRangeIncludingBlanks(
    effectiveDate,
    courseEndDateString,
    course.attributes.calendar.dates,
    sourceDatesOff,
    sourceRotationCalendar.attributes.schoolDays
  )

  // Insert the transferrable array into the updated course at effectiveDate.
  const destinationDatesOff = consolidateCourseDatesOff(
    updatedCoursePart1,
    destinationPlanbook,
    destinationRotationCalendar
  )
  const newCourseDates = insertRange(
    transferrableArray,
    effectiveDate,
    0,
    null,
    updatedCoursePart1.attributes.calendar.dates,
    destinationDatesOff,
    destinationRotationCalendar.attributes.schoolDays,
    false
  )

  return constructUnitsAndDates(
    newCourseDates,
    destinationDatesOff,
    destinationRotationCalendar.attributes.schoolDays
  )
}
